import React, { useMemo, useLayoutEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { change, Field, formValueSelector, reduxForm } from 'redux-form';
import { Button, Form, FormGroup, InputGroupText } from 'reactstrap';
import {
  clearUsers,
  getExpenseAuthorizerUserByCostCenter,
} from '../../../../../actions';
import TextInput from '../../../../common/forms/input/TextInput';
import { isRequired, validateMinNumber } from '../../../../../utils/validators';
import FormItem from '../../../../common/forms/FormItem';
import FormFooter from '../../../../common/forms/FormFooter';
import CostCenterSelect from '../../../../common/forms/select/CostCenterSelect';
import ExpenseTypeSelect from '../../../../common/forms/select/ExpenseTypeSelect';
import Select from '../../../../common/forms/select/Select';
import { CURRENCY } from '../../../../../config/locale';
import {
  CREDIT_NOTE_MOTIVES_TICKET,
  TICKET_STATUS,
} from '../../../../../config/constants';
import { enumToSelectOptions } from '../../../../../utils/enum';

const selector = formValueSelector('RefundForm');

const validateMin = validateMinNumber(0);

const validateMaxNumber = (max) => (value) =>
  value && Number(value) > max ? `Debe ser menor o igual a ${max}` : undefined;

const RefundForm = ({
  initialValues,
  status,
  userOptions,
  dispatchClearUsers,
  dispatchGetExpenseAuthorizerUserByCostCenter,
  dispatchChange,
  handleSubmit,
  creditNoteMotiveValue,
  onCancel,
}) => {
  const maxMonto = initialValues.salePrice;
  const validations = useMemo(
    () => [validateMin, validateMaxNumber(maxMonto), isRequired],
    [],
  );

  useLayoutEffect(
    () => () => {
      dispatchClearUsers();
    },
    [],
  );

  const handleCostCenterChange = async (costCenterOption) => {
    const costCenterId = costCenterOption.value;
    if (costCenterId) {
      dispatchChange('RefundForm', 'authorizerUserId', null);

      await dispatchGetExpenseAuthorizerUserByCostCenter({ costCenterId });
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <FormGroup row className="mt-4">
        <FormItem label="Tipo de Egreso" required>
          <Field
            name="expenseTypeId"
            component={ExpenseTypeSelect}
            validate={[isRequired]}
          />
        </FormItem>
      </FormGroup>
      <FormGroup row>
        <FormItem label="Centro de Costos" required>
          <Field
            name="costCenterId"
            component={CostCenterSelect}
            onChange={handleCostCenterChange}
            validate={[isRequired]}
          />
        </FormItem>
      </FormGroup>
      <FormGroup row>
        <FormItem label="Autorizador" required>
          <Field
            name="authorizerUserId"
            component={Select}
            options={userOptions}
            validate={[isRequired]}
          />
        </FormItem>
      </FormGroup>
      <FormGroup row className="mt-4">
        <FormItem label="Descripción">
          <Field name="description" component={TextInput} type="textarea" />
        </FormItem>
      </FormGroup>
      Seleccionar el tipo de devolución que se desea realizar para el monto
      correspondiente.
      <FormGroup row className="mt-3">
        <FormItem label="Motivo de Generación" required>
          <Field
            name="creditNoteMotive"
            component={Select}
            options={enumToSelectOptions(CREDIT_NOTE_MOTIVES_TICKET)}
            placeholder="Motivo de Generación"
            validate={[isRequired]}
          />
        </FormItem>
      </FormGroup>
      {creditNoteMotiveValue &&
        creditNoteMotiveValue.value ===
          CREDIT_NOTE_MOTIVES_TICKET.OTHERS.value && (
          <FormGroup row>
            <FormItem label="Observación de Generación" required>
              <Field
                name="creditNoteMotiveDescription"
                component={TextInput}
                type="textarea"
                placeholder="Observación de Generación"
                validate={[isRequired]}
              />
            </FormItem>
          </FormGroup>
        )}
      <FormGroup row className="mt-4">
        <FormItem label="Monto" required>
          <Field
            name="salePrice"
            component={TextInput}
            type="number"
            append={<InputGroupText>{CURRENCY}</InputGroupText>}
            validate={validations}
            disabled={status !== TICKET_STATUS.ACTIVE.value}
          />
        </FormItem>
      </FormGroup>
      <FormFooter>
        <Button color="warning" onClick={onCancel}>
          Cancelar
        </Button>
      </FormFooter>
    </Form>
  );
};

RefundForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,

  status: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onCancel: PropTypes.func.isRequired,

  initialValues: PropTypes.shape({
    salePrice: PropTypes.number.isRequired,
  }).isRequired,

  userOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,

  creditNoteMotiveValue: PropTypes.instanceOf(Object),
  dispatchClearUsers: PropTypes.func.isRequired,
  dispatchGetExpenseAuthorizerUserByCostCenter: PropTypes.func.isRequired,
  dispatchChange: PropTypes.func.isRequired,
};

RefundForm.defaultProps = {
  creditNoteMotiveValue: null,
};

const mapStateToProps = (state) => ({
  userOptions: state.UserUnit.User.getIn(['all', 'content', 'content']).map(
    (user) => ({
      value: user.userId,
      label: user.fullName,
    }),
  ),
  creditNoteMotiveValue: selector(state, 'creditNoteMotive'),
});

const mapDispatchToProps = {
  dispatchGetExpenseAuthorizerUserByCostCenter:
    getExpenseAuthorizerUserByCostCenter,
  dispatchClearUsers: clearUsers,
  dispatchChange: change,
};

const formComponent = reduxForm({
  form: 'RefundForm',
})(RefundForm);

export default connect(mapStateToProps, mapDispatchToProps)(formComponent);
